import React, { useEffect, useState } from 'react';
import { useApi } from '~/contexts';
import {
  Grid,
  Card,
  CardLabel,
  CardValue,
  CardSubValue,
  Section,
  SectionTitle,
  formatCost,
  formatNumber,
} from './shared';
import CostOverTime from './CostOverTime';

export default function CostSummary({ start, end, granularity }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getSummary({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) {
    return (
      <Section>
        <SectionTitle>Cost &amp; Tokens</SectionTitle>
        <Grid>
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardLabel>&nbsp;</CardLabel>
              <CardValue>&mdash;</CardValue>
            </Card>
          ))}
        </Grid>
      </Section>
    );
  }

  const days = Math.max(1, data.daysWithData);

  return (
    <>
      <Section>
        <SectionTitle>Cost &amp; Tokens</SectionTitle>
        <Grid>
          <Card>
            <CardLabel>Total Cost</CardLabel>
            <CardValue>{formatCost(data.totalCost)}</CardValue>
            <CardSubValue>
              Input: <strong>{formatCost(data.inputCost)}</strong> / Cached:{' '}
              <strong>{formatCost(data.cachedInputCost)}</strong> / Output:{' '}
              <strong>{formatCost(data.outputCost)}</strong>
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>
              Avg Cost per Day ({formatNumber(data.daysWithData)} {data.daysWithData === 1 ? 'day' : 'days'})
            </CardLabel>
            <CardValue>{formatCost(data.totalCost / days)}</CardValue>
            <CardSubValue>
              Input: <strong>{formatCost(data.inputCost / days)}</strong> / Cached:{' '}
              <strong>{formatCost(data.cachedInputCost / days)}</strong> / Output:{' '}
              <strong>{formatCost(data.outputCost / days)}</strong>
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>Total Tokens</CardLabel>
            <CardValue>{formatNumber(data.totalInputTokens + data.totalOutputTokens)}</CardValue>
            <CardSubValue>
              Input: <strong>{formatNumber(data.totalInputTokens - data.cachedInputTokens)}</strong> / Cached:{' '}
              <strong>{formatNumber(data.cachedInputTokens)}</strong> / Output:{' '}
              <strong>{formatNumber(data.totalOutputTokens)}</strong>
            </CardSubValue>
          </Card>
        </Grid>
        <CostOverTime start={start} end={end} granularity={granularity} />
      </Section>
      <Section>
        <SectionTitle>Cache Efficiency</SectionTitle>
        <Grid>
          <Card>
            <CardLabel>Cache Hit Rate</CardLabel>
            <CardValue>
              {data.totalInputTokens > 0 ? ((data.cachedInputTokens / data.totalInputTokens) * 100).toFixed(1) : '0.0'}%
            </CardValue>
            <CardSubValue>
              <strong>{formatNumber(data.cachedInputTokens)}</strong> of{' '}
              <strong>{formatNumber(data.totalInputTokens)}</strong> input tokens from cache
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>Cache Savings</CardLabel>
            <CardValue>{formatCost(data.fullInputCost - data.inputCost - data.cachedInputCost)}</CardValue>
            <CardSubValue>
              Would have cost <strong>{formatCost(data.fullInputCost)}</strong> without caching
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>Savings Rate</CardLabel>
            <CardValue>
              {data.fullInputCost > 0
                ? (((data.fullInputCost - data.inputCost - data.cachedInputCost) / data.fullInputCost) * 100).toFixed(1)
                : '0.0'}
              %
            </CardValue>
            <CardSubValue>
              Paying <strong>{formatCost(data.inputCost + data.cachedInputCost)}</strong> of{' '}
              <strong>{formatCost(data.fullInputCost)}</strong> potential input cost
            </CardSubValue>
          </Card>
        </Grid>
      </Section>
    </>
  );
}
